import moment from 'moment';
import React from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { CgClose } from 'react-icons/cg';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import { IoCalendarOutline } from 'react-icons/io5';
import { MdOutlineArrowBackIos } from 'react-icons/md';
import { VscRefresh } from 'react-icons/vsc';

import { DayPicker } from 'components/DayPicker';
import { DatePickerFormats } from 'utils/enums/time-format';
import { TimeRange } from 'utils/time';
import DatePickerFooter from 'views/MTable/TableFilters/MDateRangeFilterButton/DatePickerFooter/DatePickerFooter';
import {
  DateRangeStepType,
  OnClick,
  TypesFilterButton,
} from 'views/MTable/TableFilters/MDateRangeFilterButton/MDateRangeFilterButton.model';

import classes from './DatetimeRangePicker.module.scss';

type Props = {
  dropdownRef: React.RefObject<HTMLDivElement>;
  datePickerRef: React.RefObject<HTMLDivElement>;
  typePicker: boolean;
  isOpen: boolean;
  disableMenu?: boolean;
  dateRangeStepType?: DateRangeStepType;
  dateRange: DateRange | undefined;
  pickerDateRange: DateRange | undefined;
  timeRange: TimeRange;
  onChangeDate: (type: TypesFilterButton) => void;
  updateDateTimeRange: (dateRange: DateRange | undefined, timeRange: TimeRange) => void;
  onChangeTypePicker: (event: OnClick) => void;
  onResetDate?: () => void;
  onOpen: () => void;
  onPrevDate: () => void;
  onNextDate: () => void;
  onClose: () => void;
};

const DatetimeRangePickerView: React.FC<Props> = (props: React.PropsWithChildren<Props>) => {
  const { t } = useTranslation();

  return (
    <div className={classes['container']}>
      <div className={classes['btnDateRange']} onClick={props.onOpen}>
        {`${moment(props.dateRange?.from).format(DatePickerFormats.GeneralDatePicker)} ${moment(props.timeRange?.from, 'HH:mm').format(
          DatePickerFormats.TimePicker,
        )} `}
        -
        {` ${moment(props.dateRange?.to).format(DatePickerFormats.GeneralDatePicker)} ${moment(props.timeRange?.to, 'HH:mm').format(
          DatePickerFormats.TimePicker,
        )}`}
        <VscRefresh
          onClick={(event) => {
            event.stopPropagation();
            props.onResetDate?.();
          }}
          className={classes['btnRefresh']}
        />
      </div>

      <div className={classes['control']}>
        <IoIosArrowBack className={classes['btn']} onClick={props.onPrevDate}></IoIosArrowBack>
        <IoIosArrowForward className={classes['btn']} onClick={props.onNextDate}></IoIosArrowForward>
      </div>

      {props.isOpen && !props.disableMenu ? (
        props.typePicker ? (
          <div ref={props.datePickerRef} className={classes['contentPicker']} onClick={(e) => e.stopPropagation()}>
            <div className={classes['headerActions']}>
              <MdOutlineArrowBackIos className={classes['headerActions--icon']} onClick={props.onChangeTypePicker}></MdOutlineArrowBackIos>
              <CgClose className={classes['headerActions--icon']} onClick={props.onClose}></CgClose>
            </div>
            <DayPicker
              mode="range"
              defaultMonth={new Date()}
              selected={props.pickerDateRange}
              modifiersClassNames={{ selected: classes['mySelected'], today: classes['myToday'] }}
              onSelect={(dateRange?: DateRange) => props.updateDateTimeRange(dateRange, props.timeRange)}
              footer={
                <DatePickerFooter
                  range={props.timeRange}
                  setRange={(range: TimeRange) => props.updateDateTimeRange(props.dateRange, range)}
                />
              }
            />
          </div>
        ) : (
          <div ref={props.dropdownRef} className={classes['contentButtons']}>
            <div className={classes['btn']} onClick={() => props.onChangeDate(TypesFilterButton.Today)}>
              <span className={classes['btn--text']}>{t('filters.filterDate.today')}</span>
            </div>
            <div className={classes['btn']} onClick={() => props.onChangeDate(TypesFilterButton.Tomorrow)}>
              <span className={classes['btn--text']}>{t('filters.filterDate.tomorrow')}</span>
            </div>
            <div className={classes['btn']} onClick={() => props.onChangeDate(TypesFilterButton.Yesterday)}>
              <span className={classes['btn--text']}>{t('filters.filterDate.yesterday')}</span>
            </div>
            <div className={classes['btn']} onClick={() => props.onChangeDate(TypesFilterButton.Week)}>
              <span className={classes['btn--text']}>{t('filters.filterDate.lastWeek')}</span>
            </div>
            <div className={classes['btn']} onClick={() => props.onChangeDate(TypesFilterButton.Month)}>
              <span className={classes['btn--text']}>{t('filters.filterDate.lastMonth')}</span>
            </div>
            <div className={classes['btn']} onClick={props.onChangeTypePicker}>
              <IoCalendarOutline className={classes['btn--icon']} />
              <span className={classes['btn--text--withIcon']}>{t('filters.filterDate.customRange')}</span>
            </div>
          </div>
        )
      ) : null}
    </div>
  );
};

DatetimeRangePickerView.displayName = 'DatetimeRangePickerView';
DatetimeRangePickerView.defaultProps = {};

export default DatetimeRangePickerView;
